import React from 'react';
import { shape } from 'prop-types';
import { loadable, LazyHydrate as Hydrator } from 'nordic/lazy';
import View from '../view';

import Snackbar from '../../features/snackbar';

// Head Section
import Onboarding from '../../features/onboarding/onboarding.mobile';

import KYCNotification from '../../features/kyc-notification/kyc-notification.mobile';

import Gallery from '../../features/gallery/gallery.mobile';

import ModelsCarouselMobile from '../../features/models-carousel/models-carousel.mobile';
import MultifamilyModelsCarousel from '../../features/mf-models-carousel/mf-models-carousel.mobile';
import MaintenanceFeeLTR from '../../features/maintenance-feel-ltr/maintenance-fee-ltr';
import AvailableUnitsLabel from '../../features/available-units-label/available-units-label';
import OnlineVisitRequestLabel from '../../features/online-visit-request-label/online-visit-request-label';
import SellerReputationBanner, {
  isSellerReputationBannerVisible,
} from '../../features/seller-reputation-banner/seller-reputation-banner';
import Denounce from '../../features/denounce';
import PiStatistics from '../../features/pi-statistics/pi-statistics';
import BillboardAdn from '../../features/billboard-adn/billboard-adn-bottom';
import QuotationLimitMessage from '../../features/quotation-message/quotation-limit-message';
import QuotationSellerMessage from '../../features/quotation-message/quotation-seller-message';
import Grid from '../../../../../components/grid';
import SellerGoodAttention from '../../features/seller-good-attention/seller-good-attention';
import SmartTabs from '../../features/smart-tabs/smart-tabs';

// Utils
import classnames from 'classnames';

const ReportProblem = loadable(() => import('../../features/report-problem/report-problem.mobile'));
const LabelRentalType = loadable(() => import('../../features/label-rental-type'));

const PriceComparison = loadable(() => import('../../features/price-comparison'));
const PriceComparisonRex = loadable(() => import('../../features/price-comparison-rex'));
const ExternalCredits = loadable(() => import('../../features/external-credits'));
const SellerProfile = loadable(() => import('../../features/seller-profile/seller-profile.mobile'));
const SellerProfileREX = loadable(() => import('../../features/seller-profile/seller-profile-REX'));
const GroupedMainActions = loadable(() => import('../../features/grouped-main-actions'));
const BuyBenefits = loadable(() => import('../../features/buy-benefits/buy-benefits.mobile'));
const GroupedShareBookmark = loadable(() => import('../../features/grouped-share-bookmark'));
const GiftRegistry = loadable(() => import('../../features/gift-registry'));
const OnlineRentalDetails = loadable(() => import('../../features/online-rental-details/online-rental-details.mobile'));
const LTRRequirementsBullet = loadable(() =>
  import('../../features/ltr-requirements-bullet/ltr-requirements-bullet.mobile'),
);

const ItemStatusMessageHead = loadable(() => import('../../features/item-status/head'));
// Features, Description, Returns & Box Components
const AllFeatures = loadable(() => import('../../features/all-features/all-features.mobile'));
const HighlightedSpecsAttrs = loadable(() => import('../../features/highlighted-specs/highlighted-specs-attrs.mobile'));
const HighlightedSpecsRex = loadable(() => import('../../features/highlighted-specs-rex/highlighted-specs-rex.mobile'));
const Description = loadable(() => import('../../features/description/description.mobile'));
const PointsOfInterest = loadable(() => import('../../features/points-of-interest/point-of-interest.mobile'));
const WhatsappCard = loadable(() => import('../../features/whatsapp-card/whatsapp-card'));
const DescriptionREX = loadable(() => import('../../features/description/description-rex.mobile'));
const RequirementsCardLTR = loadable(() => import('../../features/requirements-card-ltr/requirements-card-ltr.mobile'));
const RequirementsCardLTRNew = loadable(() =>
  import('../../features/requirements-card-ltr-new/requirements-card-ltr.mobile'),
);

// Payment Methods, Questions, Reviews, RelatedServices
const Questions = loadable(() => import('../../features/questions-vis/questions-vis.mobile'));
const QuestionsUnregisteredMobile = loadable(() => import('../../features/questions/questions-unregistered-vip'));
const SecurityTips = loadable(() => import('../../features/security-tips/security-tips.mobile'));
const RelatedServices = loadable(() => import('../../features/related-services'));

// Content Bottom
const FullscreenAd = loadable(() => import('../../features/fullscreen-ad'));
const Breadcrumb = loadable(() => import('../../features/breadcrumb'));

// Recommendations
const RecommendationsCarouselFree = loadable(() => import('../../features/recommendations/carousel-free'));

// Hero
const Header = loadable(() => import('../../features/header/header.mobile'));

// Short Description
const Price = loadable(() => import('../../features/price/price.mobile'));
const CouponSummary = loadable(() =>
  import('../../../../../pages/vpp/features/buybox/coupon-summary/coupon-summary.mobile'),
);
const PriceComparisonLink = loadable(() => import('../../features/price-comparison-link'));
const HighlightedSpecsRes = loadable(() => import('../../features/highlighted-specs-res'));
const ItemStatusShortDescription = loadable(() => import('../../features/item-status/short-description'));
const Location = loadable(() => import('../../features/location/location.mobile'));
const LocationAndPoints = loadable(() => import('../../features/location-and-points/location-and-points.mobile'));
const AvailableUnits = loadable(() => import('../../features/available-units/available-units.mobile'));

const RealEstateMobile = props => {
  const { initialState } = props;
  const isSellerReputationBannerVisibleFlag = isSellerReputationBannerVisible(initialState?.components);

  return (
    <View {...props} vertical="real-estate">
      <Grid.Container className="ui-pdp-container--top">
        {/* Head Section */}
        <Onboarding />
        <Hydrator whenVisible>
          <ItemStatusMessageHead />
        </Hydrator>
        <Hydrator whenIdle>
          <RecommendationsCarouselFree
            featureName="carousel_classifieds_res_vip_paused"
            className="ui-pdp-carousel-not-active"
            lazy={false}
            preload
            fetchPriority="high"
            criticalImages={1}
          />
        </Hydrator>
        <Hydrator whenIdle>
          <RecommendationsCarouselFree
            featureName="carousel_classifieds_res_ltr_vip_paused"
            className="ui-pdp-carousel-not-active"
            lazy={false}
            preload
            fetchPriority="high"
            criticalImages={1}
          />
        </Hydrator>
        <Hydrator whenVisible>
          <KYCNotification />
        </Hydrator>
        {/* Hero */}
        <Hydrator whenIdle>
          <Header />
        </Hydrator>
        <Gallery />
        {/* Short Description */}
        <Hydrator whenVisible>
          <Price />
        </Hydrator>
        <Hydrator whenVisible>
          <CouponSummary />
        </Hydrator>
        <Hydrator whenVisible>
          <MaintenanceFeeLTR />
        </Hydrator>
        <Hydrator whenVisible>
          <PriceComparisonLink />
        </Hydrator>
        <Hydrator whenVisible>
          <LabelRentalType />
        </Hydrator>
        <Hydrator whenVisible>
          <HighlightedSpecsRes />
        </Hydrator>
        <Hydrator whenVisible>
          <AvailableUnitsLabel />
        </Hydrator>
        <Hydrator whenVisible>
          <OnlineVisitRequestLabel />
        </Hydrator>
        <Hydrator whenVisible>
          <ItemStatusShortDescription />
        </Hydrator>
        <Hydrator whenVisible>
          <QuotationLimitMessage />
        </Hydrator>
        <Hydrator whenVisible>
          <QuotationSellerMessage />
        </Hydrator>
        <Hydrator whenVisible>
          <SellerReputationBanner />
        </Hydrator>
        <Hydrator whenVisible>
          <GroupedMainActions
            containerClass={classnames({
              'mt-0': isSellerReputationBannerVisibleFlag,
            })}
          />
          <BuyBenefits />
          <SellerGoodAttention />
        </Hydrator>
        <Hydrator whenVisible>
          <ReportProblem />
        </Hydrator>
        <Hydrator whenVisible>
          <LTRRequirementsBullet />
        </Hydrator>
        <Hydrator whenVisible>
          <GiftRegistry />
          <GroupedShareBookmark />
        </Hydrator>
        <Hydrator whenVisible>
          <ModelsCarouselMobile />
        </Hydrator>
        <Hydrator whenVisible>
          <RecommendationsCarouselFree
            featureName="carousel_dev_bau_classifieds_res_vip"
            className="ui-pdp-dev-bau-carousel-classified"
          />
        </Hydrator>
        <Hydrator whenVisible>
          <MultifamilyModelsCarousel />
        </Hydrator>
        {/* Recommendations */}
        {/* Features, Description, Returns & Box Components */}
        <Hydrator whenVisible>
          <AvailableUnits />
        </Hydrator>
        <Hydrator whenVisible>
          <HighlightedSpecsRex />
        </Hydrator>
        <Hydrator whenVisible>
          <Location />
        </Hydrator>
        <Hydrator whenVisible>
          <HighlightedSpecsAttrs />
        </Hydrator>
        <Hydrator whenVisible>
          <AllFeatures />
        </Hydrator>
        <Hydrator whenVisible>
          <Description className="pl-16 pr-16" />
        </Hydrator>
        <Hydrator whenVisible>
          <SellerProfileREX />
        </Hydrator>
        <Hydrator whenVisible>
          <RequirementsCardLTRNew />
        </Hydrator>
        <Hydrator whenVisible>
          <RequirementsCardLTR />
        </Hydrator>
        <Hydrator whenVisible>
          <DescriptionREX className="pl-16 pr-16" />
        </Hydrator>
        <Hydrator whenIdle>
          <PointsOfInterest />
        </Hydrator>
        <Hydrator whenIdle>
          <LocationAndPoints />
        </Hydrator>
        <Hydrator whenIdle>
          <PriceComparison />
        </Hydrator>
        <Hydrator whenVisible>
          <SellerProfile />
        </Hydrator>
        <Hydrator whenIdle>
          <PriceComparisonRex />
        </Hydrator>
        {/* Payment Methods, Questions, Reviews */}
        <Hydrator whenVisible>
          <ExternalCredits />
        </Hydrator>
        <Hydrator whenVisible>
          <OnlineRentalDetails />
        </Hydrator>
        <Hydrator whenVisible>
          <WhatsappCard />
        </Hydrator>
        <Hydrator whenIdle>
          <RelatedServices />
        </Hydrator>
        <Hydrator whenIdle>
          <Questions />
        </Hydrator>
        <Hydrator whenVisible>
          <QuestionsUnregisteredMobile />
        </Hydrator>
        <Hydrator whenVisible>
          <SecurityTips />
        </Hydrator>
        {/* Content Bottom */}
        <div className="ui-pdp-container--bottom">
          <Hydrator whenVisible>
            <Denounce />
          </Hydrator>
          <Hydrator whenVisible>
            <RecommendationsCarouselFree featureName="carousel_classifieds_res_vip" className="mb-24 mt-16" />
          </Hydrator>
          <Hydrator whenVisible>
            <Breadcrumb mode="compact" />
          </Hydrator>
          <Hydrator whenIdle>
            <FullscreenAd />
          </Hydrator>
          <Hydrator ssrOnly>
            <BillboardAdn />
          </Hydrator>
          <Hydrator whenVisible>
            <SmartTabs />
          </Hydrator>
          <Hydrator whenVisible>
            <PiStatistics />
          </Hydrator>
        </div>
      </Grid.Container>
      <Snackbar />
    </View>
  );
};

RealEstateMobile.propTypes = {
  initialState: shape().isRequired,
};

export default RealEstateMobile;
